import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"

import {
  isFrameLogoUploaded,
  isNoManagedTemplateError,
  type PresentationGenerationMode,
  presentationGenerationMode,
  specHasMarkdown,
} from "./presentation-brand-spec-utils"
import {
  usePresentationBrandQuery,
  usePresentationTemplateCatalogQuery,
  usePutPresentationBrandMutation,
} from "../hooks/admin-queries"

const MODE_REASONS: Record<PresentationGenerationMode, string> = {
  markdown: "Set new decks to markdown brand spec",
  managed: "Set new decks to always use company PowerPoint template",
  ask: "Set new decks to ask before using the company template",
}

export function PresentationGenerationMode() {
  const { t } = useTranslation()
  const brandQuery = usePresentationBrandQuery()
  const catalogQuery = usePresentationTemplateCatalogQuery()
  const putMutation = usePutPresentationBrandMutation()

  const data = brandQuery.data
  const noTemplate = catalogQuery.isError && isNoManagedTemplateError(catalogQuery.error)
  const mode = data ? presentationGenerationMode(data) : "ask"
  const markdownReady = Boolean(
    data && specHasMarkdown(data.spec_markdown) && isFrameLogoUploaded(data.assets)
  )

  const handleChange = async (next: PresentationGenerationMode) => {
    if (!data?.hash) {
      toast.error(
        t("adminDashboard.systemConfig.presentationTemplate.modeFailed", {
          defaultValue: "Failed to update how new decks are generated",
        })
      )
      return
    }
    try {
      await putMutation.mutateAsync({
        enabled: next === "markdown",
        spec_filename: data.spec_filename,
        spec_markdown: data.spec_markdown,
        generation_mode: next,
        expectedHash: data.hash,
        reason: MODE_REASONS[next],
      })
      toast.success(
        t("adminDashboard.systemConfig.presentationTemplate.modeSaved", {
          defaultValue: "New-deck route saved",
        })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.systemConfig.presentationTemplate.modeFailed", {
          defaultValue: "Failed to update how new decks are generated",
        }),
        { description: err instanceof Error ? err.message : undefined }
      )
    }
  }

  const options: {
    value: PresentationGenerationMode
    disabled: boolean
    title: string
    help: string
  }[] = [
    {
      value: "markdown",
      disabled: !markdownReady,
      title: t("adminDashboard.systemConfig.presentationTemplate.modeMarkdown", {
        defaultValue: "Markdown spec",
      }),
      help: t("adminDashboard.systemConfig.presentationTemplate.modeMarkdownHelp", {
        defaultValue: "Build from the uploaded spec and logos. No question.",
      }),
    },
    {
      value: "managed",
      disabled: noTemplate,
      title: t("adminDashboard.systemConfig.presentationTemplate.modeManaged", {
        defaultValue: "Company template",
      }),
      help: t("adminDashboard.systemConfig.presentationTemplate.modeManagedHelp", {
        defaultValue: "Always fill the company .pptx. No question.",
      }),
    },
    {
      value: "ask",
      disabled: false,
      title: t("adminDashboard.systemConfig.presentationTemplate.modeAsk", {
        defaultValue: "Ask the user",
      }),
      help: t("adminDashboard.systemConfig.presentationTemplate.modeAskHelp", {
        defaultValue: "Ask whether to use the company template before building a deck.",
      }),
    },
  ]

  return (
    <div className="border-border space-y-3 rounded-lg border p-4">
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {t("adminDashboard.systemConfig.presentationTemplate.modeTitle", {
            defaultValue: "How new decks are generated",
          })}
        </p>
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.systemConfig.presentationTemplate.modeHelp", {
            defaultValue:
              "You can keep both a markdown spec and a company .pptx. This choice is what Explorer uses.",
          })}
        </p>
      </div>
      <RadioGroup
        className="grid grid-cols-1 gap-3 sm:grid-cols-3"
        disabled={!data || putMutation.isPending || brandQuery.isLoading}
        value={mode}
        onValueChange={(value) => {
          void handleChange(value as PresentationGenerationMode)
        }}
      >
        {options.map((option) => (
          <div
            key={option.value}
            className={`flex items-start gap-3 rounded-lg border p-3 ${
              mode === option.value ? "border-primary/40 bg-accent/50" : "border-border"
            } ${option.disabled ? "opacity-50" : ""}`}
          >
            <RadioGroupItem
              className="mt-0.5"
              disabled={option.disabled || !data || putMutation.isPending}
              id={`deck-mode-${option.value}`}
              value={option.value}
            />
            <Label
              className="min-w-0 flex-1 cursor-pointer space-y-0.5 font-normal"
              htmlFor={`deck-mode-${option.value}`}
            >
              <span className="block text-sm font-medium">{option.title}</span>
              <span className="text-muted-foreground block text-xs">{option.help}</span>
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  )
}
